import { joiResolver } from "@hookform/resolvers/joi";
import Joi from "joi";
import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import {
  FaCheckCircle,
  FaExchangeAlt,
  FaExclamationTriangle,
  FaTimesCircle,
} from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";
import BusinessHourForm from "src/components/forms/BusinessHourForm";
import CountryForm from "src/components/forms/CountryForm";
import InputForm from "src/components/forms/InputForm";
import SwipetypeForm from "src/components/forms/SwipetypeForm";
import TagSelectorForm from "src/components/forms/TagSelectorForm";
import TextareaForm from "src/components/forms/TextareaForm";
import MultiImageUploader from "src/components/MultimageUpload";
import Subtitle from "src/components/Subtitle";
import Title from "src/components/Title";
import API from "src/services/api";
import { asArray, asText } from "src/utils/tableFormatter";

const SwipeCheckSite = () => {
  // Parameter swipe_id aus URL auslesen
  const { swipe_id } = useParams();
  const navigate = useNavigate();
  const [checkresults, setCheckresults] = useState([]);

  const [tripadvisorItems, setTripadvisorItems] = useState([]);
  const [step, setStep] = useState(1);
  const [trigger, setTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRejectLoading, setIsRejectLoading] = useState(false);
  const loadCheckDataTimeoutRef = useRef(null);

  // Validierungsschema
  const schema = Joi.object({
    swipetype_id: Joi.string().required(),
    name: Joi.string().min(0).max(255),
    street: Joi.string().min(0).max(255),
    street_no: Joi.string().min(0).max(20),
    postcode: Joi.string().min(0).max(11),
    city: Joi.string().min(0).max(255),
    country: Joi.string().required(),
    phone: Joi.string().min(0).max(255),
    email: Joi.string().min(0).max(255),
    website: Joi.string().min(0).max(255),
    facebook_url: Joi.string().min(0).max(255),
    instagram_url: Joi.string().min(0).max(255),
    twitter_url: Joi.string().min(0).max(255),
    tripadvisor_url: Joi.string().min(0).max(255),
    zefix_id: Joi.string().min(0).max(255),
    zefix_name: Joi.string().min(0).max(255),
    latitude: Joi.number().required(),
    longitude: Joi.number().required(),
    description: Joi.string().min(0).max(350),
    tags: Joi.array(),
    business_hours: Joi.array(),
  });

  const {
    reset,
    handleSubmit,
    register,
    watch,
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: joiResolver(schema),
    defaultValues: {
      tags: [],
      business_hours: [],
    },
  });

  const loadData = async () => {
    const res = await API.getSwipe(swipe_id);
    if (res.status === true) {
      reset({
        swipetype_id: asText(res.data?.swipetype_id),
        name: asText(res.data?.name),
        street: asText(res.data?.street),
        street_no: asText(res.data?.street_no),
        postcode: asText(res.data?.postcode),
        city: asText(res.data?.city),
        country: asText(res.data?.country),
        phone: asText(res.data?.phone),
        email: asText(res.data?.email),
        website: asText(res.data?.website),
        facebook_url: asText(res.data?.facebook_url),
        instagram_url: asText(res.data?.instagram_url),
        twitter_url: asText(res.data?.twitter_url),
        tripadvisor_url: asText(res.data?.tripadvisor_url),
        zefix_id: asText(res.data?.zefix_id),
        zefix_name: asText(res.data?.zefix_name),
        description: asText(res.data?.description),
        latitude: res.data?.latitude ?? "",
        longitude: res.data?.longitude ?? "",
        tags: asArray(res.data?.tags)
          .map((item) => item?.id)
          .filter((id) => id != null),
        business_hours: asArray(res.data?.business_hours),
      });
    }
  };

  // Init
  useEffect(() => {
    loadData();
  }, []);

  // Load notes when component is rendered the first time
  useEffect(() => {
    if (step === 2) {
      loadTripadvisorItems(
        getValues("latitude"),
        getValues("longitude"),
        getValues("city"),
        getValues("postcode"),
        getValues("name"),
      );
    }

    if (step === 3) {
      loadOeffnungszeiten(getValues());
    }

    if (step === 5) {
      loadTAImages(getValues());
    }
  }, [step]);

  // send formular data to backend when they change
  useEffect(() => {
    if (step !== 1) {
      if (loadCheckDataTimeoutRef.current) {
        clearTimeout(loadCheckDataTimeoutRef.current);
        loadCheckDataTimeoutRef.current = null;
      }
      return undefined;
    }

    const subscription = watch((values) => {
      console.log("Form changed", values);
      loadCheckData(values);
    });

    return () => {
      subscription.unsubscribe();
      if (loadCheckDataTimeoutRef.current) {
        clearTimeout(loadCheckDataTimeoutRef.current);
        loadCheckDataTimeoutRef.current = null;
      }
    };
  }, [step, watch]);

  async function fetchImageAsBlob(url) {
    const response = await fetch(url);
    if (!response.ok) throw new Error("Bild konnte nicht geladen werden");
    return await response.blob();
  }

  async function uploadImageFromUrl(imageUrl) {
    if (window.confirm("Bild importieren?")) {
      try {
        const blob = await fetchImageAsBlob(imageUrl);

        // Optional: in ein File-Objekt mit Dateinamen umwandeln
        const file = new File([blob], "image.jpg", { type: blob.type });

        const formData = new FormData();
        formData.append("source", "TRIPADVISOR");
        formData.append("images[]", file); // Der Schlüsselname hängt von deiner API ab

        const response = await API.uploadSwipeImage(swipe_id, formData);

        setTrigger((prev) => prev + 1);

        const result = await response.json();
        console.log("Upload erfolgreich:", result);
      } catch (error) {
        console.error("Fehler beim Upload:", error);
      }
    }
  }

  const loadTAImages = async (values) => {
    setIsLoading(true);
    const res = await API.getSwipeCheckDataTAImages(values);
    console.log("Check Data", res);
    setCheckresults(res.data || []);
    setIsLoading(false);
  };

  const loadOeffnungszeiten = async (values) => {
    setIsLoading(true);
    const res = await API.getSwipeCheckDataBusinessHours(values);
    console.log("Check Data", res);
    setCheckresults(res.data || []);
    setIsLoading(false);
  };

  const loadCheckData = async (values) => {
    if (loadCheckDataTimeoutRef.current) {
      clearTimeout(loadCheckDataTimeoutRef.current);
    }

    loadCheckDataTimeoutRef.current = setTimeout(async () => {
      setIsLoading(true);
      const res = await API.getSwipeCheckData(values);
      console.log("Check Data", res);
      setCheckresults(res.data || []);
      setIsLoading(false);
    }, 600);
  };

  const lat = watch("latitude");
  const long = watch("longitude");

  const onSubmit = async (formData) => {
    let result;
    if (swipe_id) {
      result = await API.updateSwipe(swipe_id, formData);
    } else {
      result = await API.createSwipe(formData);
    }

    console.log("result", result);

    if (result.status === true && result.data.id) {
      await API.checkedSwipe(swipe_id);
      console.log("ok");
      // Redirect
      navigate(`/swipe/${result.data.id}`);
    } else {
      console.log("error");
      // Error
    }
  };

  const onReject = async () => {
    if (!swipe_id) {
      return;
    }

    const reason = window.prompt("Grund fuer die Ablehnung eingeben:", "");

    if (reason === null) {
      return;
    }

    const trimmedReason = reason.trim();

    if (!trimmedReason) {
      window.alert("Bitte einen Grund eingeben.");
      return;
    }

    setIsRejectLoading(true);
    const result = await API.rejectSwipeCheck(swipe_id, trimmedReason);
    setIsRejectLoading(false);

    if (result.status) {
      return;
    }

    window.alert("Ablehnung konnte nicht gespeichert werden.");
  };

  const loadTripadvisorItems = async (lat, long, city, postcode, name) => {
    const res = await API.getTripadvisorItems(lat, long, city, postcode, name);
    console.log("loadTripadvisorItems", res);
    setTripadvisorItems(res.data || []);
  };

  const statusIcon = (status) => {
    switch (status) {
      case "FAIL":
        return (
          <span style={{ color: "red" }}>
            <FaTimesCircle />
          </span>
        );
      case "WARN":
        return (
          <span style={{ color: "orange" }}>
            <FaExclamationTriangle />
          </span>
        );
      case "OK":
        return (
          <span style={{ color: "green" }}>
            <FaCheckCircle />
          </span>
        );
      default:
        return <span>{status}</span>;
    }
  };

  const setSuggestionValues = (values) => {
    for (const [key, value] of Object.entries(values)) {
      if (key.includes("business_hours")) {
        const current = getValues(key) || [];
        setValue(key, [
          ...current,
          ...(Array.isArray(value) ? value : [value]),
        ]);
      } else {
        setValue(key, value);
      }
    }
  };

  console.log("Error", errors);
  console.log("values", watch());

  return (
    <Row>
      <Col>
        <Row className="align-items-center mb-2">
          <Col>
            <Title>Swipe prüfen</Title>
          </Col>
          <Col xs="auto">
            <Button
              variant="outline-danger"
              onClick={onReject}
              disabled={isRejectLoading}
            >
              {isRejectLoading ? "Ablehnen..." : "Ablehnen"}
            </Button>
          </Col>
        </Row>
        <Form onSubmit={handleSubmit(onSubmit)} onReset={reset}>
          <Row className={step !== 1 ? "d-none" : ""}>
            <Col>
              <Row>
                <Subtitle>Schritt 1: Stammdaten prüfen</Subtitle>
              </Row>
              <Row>
                <Col>
                  <SwipetypeForm
                    id="swipetype_id"
                    label="Swipe-Typ"
                    control={control}
                    errors={errors}
                  />

                  <InputForm
                    id="name"
                    label="Name"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <InputForm
                    id="street"
                    label="Strasse"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="street_no"
                    label="Strassennummer"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <InputForm
                    id="postcode"
                    label="PLZ"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="city"
                    label="Stadt"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <CountryForm
                    id="country"
                    label="Land"
                    control={control}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <InputForm
                    id="phone"
                    label="Telefon"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="email"
                    label="E-Mail"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="website"
                    label="Webseite"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <InputForm
                    id="zefix_id"
                    label="Zefix ID"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="zefix_name"
                    label="Zefix Name"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="facebook_url"
                    label="Facebook URL"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="instagram_url"
                    label="Instagram URL"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="twitter_url"
                    label="Twitter URL"
                    register={register}
                    errors={errors}
                  />
                </Col>
                <Col>
                  <InputForm
                    id="tripadvisor_url"
                    label="Tripadvisor URL"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStep(2)}
                  >
                    Weiter
                  </Button>
                </Col>
              </Row>
            </Col>
            <Col>
              <Row>
                {isLoading && <Spinner animation="border" role="status" />}
                {checkresults.map((i) => (
                  <div key={i.id}>
                    {statusIcon(i.status)} {i.message}
                    {i.suggestions.map((s) => (
                      <div key={s.id} style={{ marginLeft: "20px" }}>
                        <Button
                          variant="outline-secondary"
                          onClick={() => setSuggestionValues(s.values)}
                        >
                          <FaExchangeAlt />
                        </Button>{" "}
                        {s.message}
                      </div>
                    ))}
                  </div>
                ))}
              </Row>
            </Col>
          </Row>
          <Row className={step !== 2 ? "d-none" : ""}>
            <Col>
              <Row>
                <Subtitle>Schritt 2: Mit Tripadvisor verbinden</Subtitle>
              </Row>
              <Row>
                <Col>
                  <InputForm
                    id="tripadvisor_id"
                    label="Tripadvisor ID"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>

              <Button variant="outline-secondary" onClick={() => setStep(1)}>
                Zurück
              </Button>
              <Button variant="outline-secondary" onClick={() => setStep(3)}>
                Weiter
              </Button>
            </Col>
            <Col>
              <Row>
                <Col>
                  {tripadvisorItems.map((item) => (
                    <div key={item.location_id}>
                      ID: {item.location_id}
                      <br />
                      Name: {item.name}
                      <br />
                      Distanz:({item.distance} km)
                      <br />
                      Adresse:
                      {item.address_obj?.address_string}
                      <Button
                        variant="outline-secondary"
                        onClick={() =>
                          setSuggestionValues({
                            tripadvisor_id: item.location_id,
                          })
                        }
                      >
                        <FaExchangeAlt />
                      </Button>
                    </div>
                  ))}
                </Col>
              </Row>
            </Col>
          </Row>
          <Row className={step !== 3 ? "d-none" : ""}>
            <Col>
              <Row>
                <Subtitle>Schritt 3: Öffnungszeiten prüfen</Subtitle>
              </Row>
              <Row>
                <Col>
                  <TextareaForm
                    id="description"
                    label="Beschreibung"
                    register={register}
                    errors={errors}
                  />
                </Col>
              </Row>
              <Row>
                <BusinessHourForm
                  id="business_hours"
                  swipeId={swipe_id}
                  setValue={setValue}
                  watch={watch}
                />
              </Row>

              <Button variant="outline-secondary" onClick={() => setStep(2)}>
                Zurück
              </Button>
              <Button variant="outline-secondary" onClick={() => setStep(4)}>
                Weiter
              </Button>
            </Col>
            <Col>
              <Row>
                {isLoading && <Spinner animation="border" role="status" />}
                {checkresults.map((i) => (
                  <div key={i.id}>
                    {statusIcon(i.status)} {i.message}
                    {i.suggestions.map((s) => (
                      <div key={s.id} style={{ marginLeft: "20px" }}>
                        <Button
                          variant="outline-secondary"
                          onClick={() => setSuggestionValues(s.values)}
                        >
                          <FaExchangeAlt />
                        </Button>{" "}
                        {s.message}
                      </div>
                    ))}
                  </div>
                ))}
              </Row>
            </Col>
          </Row>
          <Row className={step !== 4 ? "d-none" : ""}>
            <Col>
              <Row>
                <Subtitle>Schritt 4: Tags prüfen</Subtitle>
              </Row>

              <Row>
                <Col>
                  <TagSelectorForm
                    id="tags"
                    control={control}
                    setValue={setValue}
                    watch={watch}
                    swipetype_id={getValues("swipetype_id")}
                  />
                </Col>
                <Col />
              </Row>

              <Button variant="outline-secondary" onClick={() => setStep(3)}>
                Zurück
              </Button>
              <Button variant="outline-secondary" onClick={() => setStep(5)}>
                Weiter
              </Button>
            </Col>
          </Row>
          <Row className={step !== 5 ? "d-none" : ""}>
            <Col>
              <Row>
                <Subtitle>Schritt 5: Bilder prüfen</Subtitle>
              </Row>

              <Row>
                <Col>
                  <MultiImageUploader swipeId={swipe_id} trigger={trigger} />
                </Col>
                <Col />
              </Row>

              <Button variant="outline-secondary" onClick={() => setStep(4)}>
                Zurück
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => onSubmit(getValues())}
              >
                Abschliessen
              </Button>
            </Col>
            <Col>
              <Row>
                {isLoading && <Spinner animation="border" role="status" />}
                {checkresults.map((i) =>
                  i.suggestions.map((s) => (
                    <Col key={i.id} md={4} style={{ marginBottom: "10px" }}>
                      <img
                        src={s.values.url}
                        style={{ width: "100%" }}
                        onClick={() => uploadImageFromUrl(s.values.url)}
                      />
                    </Col>
                  )),
                )}
              </Row>
            </Col>
          </Row>
        </Form>
      </Col>
    </Row>
  );
};

export default SwipeCheckSite;
